/**
 * Uklanjanje ličnih podataka iz Sentry događaja (GDPR): korisnik ostaje samo ID, nema e-pošte, imena, IP-a,
 * cookieja, tijela zahtjeva ni tokena (Authorization, `?token=` u URL-ovima). Bez zavisnosti od Sentry SDK-a
 * da bi se moglo testirati i koristiti i kad Sentry nije učitan.
 */

const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const BEARER = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi;
const SECRET_PARAM =
  /\b((?:access_|refresh_|session_|csrf_|id_)?token|password|passwd|secret|code|signature|api_?key|key|email|X-Amz-[A-Za-z-]+)=([^&\s#"']+)/gi;
const FILTERED = '[Filtered]';

const SENSITIVE_HEADERS = new Set([
  'authorization',
  'proxy-authorization',
  'cookie',
  'set-cookie',
  'x-csrf-token',
  'x-forwarded-for',
  'x-real-ip',
]);

/** Maskira e-poštu, Bearer tokene i tajne parametre u slobodnom tekstu. */
export function scrubText(s: string): string;
export function scrubText(s: string | undefined): string | undefined;
export function scrubText(s: string | undefined): string | undefined {
  if (!s) return s;
  return s
    .replace(EMAIL, FILTERED)
    .replace(BEARER, `$1 ${FILTERED}`)
    .replace(SECRET_PARAM, `$1=${FILTERED}`);
}

/** URL bez korisnika/lozinke, fragmenta i tajnih query parametara (`?token=…`). */
export function scrubUrl(raw: string): string;
export function scrubUrl(raw: string | undefined): string | undefined;
export function scrubUrl(raw: string | undefined): string | undefined {
  if (!raw) return raw;
  try {
    const u = new URL(raw, 'http://relative.invalid');
    u.username = '';
    u.password = '';
    u.hash = '';
    const out = scrubText(u.toString());
    return raw.startsWith('http') ? out : out.replace('http://relative.invalid', '');
  } catch {
    return scrubText(raw);
  }
}

interface Crumb {
  message?: string;
  data?: Record<string, unknown>;
}

/** Minimalni oblik Sentry događaja koji se čisti (kompatibilan sa `ErrorEvent` iz @sentry/*). */
export interface ScrubbableEvent {
  message?: string;
  user?: { id?: string | number; [k: string]: unknown };
  request?: {
    url?: string;
    query_string?: unknown;
    cookies?: unknown;
    data?: unknown;
    headers?: Record<string, string>;
    env?: unknown;
  };
  exception?: { values?: { value?: string }[] };
  breadcrumbs?: Crumb[];
  extra?: Record<string, unknown>;
  tags?: Record<string, unknown>;
  transaction?: string;
}

function scrubData(data: Record<string, unknown> | undefined): void {
  if (!data) return;
  for (const [k, v] of Object.entries(data)) {
    if (typeof v !== 'string') continue;
    data[k] = k === 'url' || k === 'from' || k === 'to' ? scrubUrl(v) : scrubText(v);
  }
}

/** Čisti događaj na mjestu i vraća ga (za `beforeSend` / `beforeSendTransaction`). */
export function scrubEvent<E extends ScrubbableEvent>(event: E): E {
  if (event.user) {
    const id = event.user.id;
    event.user = id === undefined ? {} : { id };
  }
  event.message = scrubText(event.message);
  if (event.transaction) event.transaction = scrubUrl(event.transaction);
  for (const ex of event.exception?.values ?? []) ex.value = scrubText(ex.value);
  if (event.request) {
    const r = event.request;
    delete r.cookies;
    delete r.data;
    delete r.env;
    r.url = scrubUrl(r.url);
    if (typeof r.query_string === 'string') r.query_string = scrubText(r.query_string);
    else delete r.query_string;
    if (r.headers) {
      for (const k of Object.keys(r.headers)) {
        if (SENSITIVE_HEADERS.has(k.toLowerCase())) delete r.headers[k];
        else r.headers[k] = scrubText(r.headers[k] ?? '');
      }
    }
  }
  for (const b of event.breadcrumbs ?? []) {
    b.message = scrubText(b.message);
    scrubData(b.data);
  }
  scrubData(event.extra);
  if (event.tags) {
    for (const k of Object.keys(event.tags)) {
      const lk = k.toLowerCase();
      if (lk.includes('email') || lk.includes('name') || lk === 'ip' || lk.includes('token'))
        delete event.tags[k];
    }
  }
  return event;
}

/** Breadcrumb (navigacija, fetch, konzola) bez tokena i e-pošte. */
export function scrubBreadcrumb<B extends Crumb>(b: B): B {
  b.message = scrubText(b.message);
  scrubData(b.data);
  return b;
}
